import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

// --- Type Definitions ---

export interface KstDataGridColumn {
  field: string;
  name: string;
  size?: string; // 'hidden' to hide
  sortable?: boolean;
  fullWidth?: boolean;
  template?: (row: any) => string;
}

export interface KstDataGridPager {
  curPage?: number;
  numPages?: number;
  records?: number;
  isVisible?: boolean;
  pageSize?: number;
}

export interface KstDataGridRowAction {
  name: string;
  icon?: string;
  event: string;
  class?: string;
  iconOnly?: boolean;
  mobileOnly?: boolean;
}

export interface KstDataGridHeaderButton {
  name: string;
  function: string;
  src?: string;
}

export interface KstDataGridRows {
  entries: any[] | null;
}

export interface KstDataGridProps {
  title?: string;
  columns: KstDataGridColumn[];
  rows: KstDataGridRows;
  headerbuttons?: KstDataGridHeaderButton[];
  pager?: KstDataGridPager;
  showcheckbox?: boolean;
  showordercolumn?: boolean;
  showstatustoggle?: boolean;
  loading?: boolean;
  mobilecolumns?: KstDataGridColumn[] | null;
  mobiletitlefield?: string | null;
  showheader?: boolean;
  sortfield?: string;
  sortdirection?: string;
  rowactions?: KstDataGridRowAction[] | null;
  mobileactionsonly?: boolean;
  expandable?: boolean;
  mobilekeycolumns?: string[] | null;
  onButtonCommand?: (functionName: string, checkedItems: any[], currentPage: number) => void;
  onRowMoved?: (row: any) => void;
  onStatusSwitch?: (id: any, newStatus: boolean) => void;
  onGoToPage?: (page: number) => void;
  onSort?: (field: string) => void;
  onRowAction?: (payload: { action: string; row: any }) => void;
  onRowClick?: (row: any) => void;
  onCellClick?: (payload: { row: any; column: any; field: string }) => void;
}

// --- Utility ---

function orderBy(arr: any[], fields: string[], directions?: string[]): any[] {
  return arr.slice().sort((a, b) => {
    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const dir = (directions && directions[i] === 'desc') ? -1 : 1;
      const aVal = a[field];
      const bVal = b[field];
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
    }
    return 0;
  });
}

function getInitialTheme(): string {
  const themeVal = localStorage.getItem('OrnaThemeColor') || '';
  const themeMap: Record<string, string> = {
    '': '',
    'kendo.customGrey': 'theme-gray-dark',
    'kendo.custom': 'theme-blue-dark',
  };
  return themeMap[themeVal] || '';
}

// --- Component ---

export const KstDataGrid = memo(function KstDataGrid({
  title = '',
  columns,
  rows,
  headerbuttons = [],
  pager = { curPage: 1, numPages: 1, records: 0, isVisible: false },
  showcheckbox = false,
  showordercolumn = false,
  showstatustoggle = false,
  loading = false,
  mobilecolumns = null,
  mobiletitlefield = null,
  showheader = true,
  sortfield = '',
  sortdirection = 'asc',
  rowactions = null,
  mobileactionsonly = false,
  expandable = false,
  mobilekeycolumns = null,
  onButtonCommand,
  onRowMoved,
  onStatusSwitch,
  onGoToPage,
  onSort,
  onRowAction,
  onRowClick,
  onCellClick,
}: KstDataGridProps) {
  const { theme } = useTheme();

  const [allChecked, setAllChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentTheme, setCurrentTheme] = useState(getInitialTheme);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string | number, boolean>>({});
  const [, setCheckTick] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const originalListIdxRef = useRef<number | null>(null);
  const focusedRowCodeRef = useRef<any>(null);

  // --- Computed values ---

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.size !== 'hidden');
  }, [columns]);

  const desktopRowActions = useMemo(() => {
    if (!rowactions) return null;
    const actions = rowactions.filter(action => !action.mobileOnly);
    return actions.length > 0 ? actions : null;
  }, [rowactions]);

  const mobileDisplayColumns = useMemo(() => {
    if (mobilecolumns) {
      return mobilecolumns;
    }
    return visibleColumns.filter(col => col.field !== mobiletitlefield);
  }, [mobilecolumns, visibleColumns, mobiletitlefield]);

  const mobileKeyDisplayColumns = useMemo(() => {
    if (mobilekeycolumns && mobilekeycolumns.length > 0) {
      return mobilekeycolumns
        .map(field => mobileDisplayColumns.find(col => col.field === field))
        .filter((col): col is KstDataGridColumn => !!col);
    }
    return mobileDisplayColumns.slice(0, 3);
  }, [mobilekeycolumns, mobileDisplayColumns]);

  const mobileExpandedColumns = useMemo(() => {
    if (mobilekeycolumns && mobilekeycolumns.length > 0) {
      return mobileDisplayColumns.filter(col =>
        !mobilekeycolumns.includes(col.field)
      );
    }
    return mobileDisplayColumns.slice(3);
  }, [mobilekeycolumns, mobileDisplayColumns]);

  const rowEntries = useMemo(() => {
    const entries = rows.entries || [];
    if (sortfield && entries.length > 0) {
      return orderBy(entries, [sortfield], [sortdirection]);
    }
    return entries;
  }, [rows.entries, sortfield, sortdirection]);

  const hasRows = useMemo(() => rowEntries.length > 0, [rowEntries]);

  const paginationInfo = useMemo(() => {
    const total = pager.records || rowEntries.length;
    const page = currentPage;
    const perPage = pager.pageSize || rowEntries.length || 50;
    const start = ((page - 1) * perPage) + 1;
    const end = Math.min(start + rowEntries.length - 1, total);
    return `Showing ${start} - ${end} of ${total} items`;
  }, [pager.records, pager.pageSize, rowEntries.length, currentPage]);

  // --- Methods ---

  const getCellValue = useCallback((row: any, column: KstDataGridColumn): string => {
    if (column.template) {
      return column.template(row);
    }
    return row[column.field] ?? '';
  }, []);

  const getCellTitle = useCallback((row: any, column: KstDataGridColumn): string => {
    const value = getCellValue(row, column);
    if (!value) return '';
    const strValue = String(value);
    if (/<(input|select|button|div[^>]*class="[^"]*select)/i.test(strValue)) {
      return '';
    }
    return strValue.replace(/<[^>]*>/g, '').trim();
  }, [getCellValue]);

  const hasCellValue = useCallback((row: any, column: KstDataGridColumn): boolean => {
    const value = getCellValue(row, column);
    if (!value) return false;
    const strValue = String(value);
    if (/<(img|input|button|select|video|audio|iframe)[^>]*>/i.test(strValue)) {
      return true;
    }
    const stripped = strValue.replace(/<[^>]*>/g, '').trim();
    return stripped.length > 0;
  }, [getCellValue]);

  const getCheckedItems = useCallback(() => {
    return rowEntries.filter((r: any) => r.isChecked === true);
  }, [rowEntries]);

  const getMobileTitleValue = useCallback((row: any): string => {
    if (mobiletitlefield) {
      const col = columns.find(c => c.field === mobiletitlefield);
      if (col) {
        return getCellValue(row, col);
      }
      return row[mobiletitlefield] ?? '';
    }
    if (visibleColumns.length > 0) {
      return getCellValue(row, visibleColumns[0]);
    }
    return '';
  }, [mobiletitlefield, columns, visibleColumns, getCellValue]);

  const getRowKey = useCallback((row: any, index: number): string | number => {
    return row.id || row.code || index;
  }, []);

  const isRowExpanded = useCallback((row: any, index: number): boolean => {
    const key = getRowKey(row, index);
    return !!expandedRows[key];
  }, [expandedRows, getRowKey]);

  // --- Scroll management ---

  const checkScrollPosition = useCallback(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const scrollLeft = tableContainer.scrollLeft;
      const scrollWidth = tableContainer.scrollWidth;
      const clientWidth = tableContainer.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < maxScroll - 1);
    }
  }, []);

  // Setup scroll listener on the table container
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const tableContainer = el.querySelector('.table-container') as HTMLDivElement;
    if (tableContainer) {
      tableContainerRef.current = tableContainer;
      const handler = () => checkScrollPosition();
      tableContainer.addEventListener('scroll', handler);
      checkScrollPosition();
      return () => {
        tableContainer.removeEventListener('scroll', handler);
      };
    }
  });

  // Theme change listener
  useEffect(() => {
    const handler = (e: Event) => {
      setCurrentTheme((e as CustomEvent).detail.theme);
    };
    window.addEventListener('vue2-theme-change', handler);
    return () => {
      window.removeEventListener('vue2-theme-change', handler);
    };
  }, []);

  // Resize listener (debounced)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => checkScrollPosition(), 150);
    };
    window.addEventListener('resize', handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', handler);
    };
  }, [checkScrollPosition]);

  // --- Event handlers ---

  const toggleSelectAll = useCallback(() => {
    const newChecked = !allChecked;
    setAllChecked(newChecked);
    for (let i = 0; i < rowEntries.length; i++) {
      rowEntries[i].isChecked = newChecked;
    }
    setCheckTick(t => t + 1);
  }, [allChecked, rowEntries]);

  const buttonCommand = useCallback((functionName: string) => {
    onButtonCommand?.(functionName, getCheckedItems(), currentPage);
  }, [onButtonCommand, getCheckedItems, currentPage]);

  const onOrderFocus = useCallback((row: any) => {
    originalListIdxRef.current = row.listIdx;
    focusedRowCodeRef.current = row.code;
  }, []);

  const rowMoved = useCallback((row: any) => {
    if (originalListIdxRef.current !== null && row.listIdx !== originalListIdxRef.current) {
      const nextRowIndex = originalListIdxRef.current;
      onRowMoved?.(row);

      // Restore focus after React re-renders
      requestAnimationFrame(() => {
        const el = rootRef.current;
        if (!el) return;
        const inputs = el.querySelectorAll('.order-input') as NodeListOf<HTMLInputElement>;
        const targetIndex = Math.min(nextRowIndex - 1, inputs.length - 1);
        if (inputs[targetIndex]) {
          inputs[targetIndex].focus();
          inputs[targetIndex].select();
        }
      });
    }
    originalListIdxRef.current = null;
    focusedRowCodeRef.current = null;
  }, [onRowMoved]);

  const toggleStatus = useCallback((row: any) => {
    const id = row.id != null ? row.id : row.revisionGroup;
    onStatusSwitch?.(id, !row.isEnabled);
  }, [onStatusSwitch]);

  const handleRowAction = useCallback((action: KstDataGridRowAction, row: any) => {
    onRowAction?.({ action: action.event, row });
  }, [onRowAction]);

  const handleRowClick = useCallback((row: any) => {
    onRowClick?.(row);
  }, [onRowClick]);

  const handleCellClick = useCallback((event: React.MouseEvent, row: any, column: KstDataGridColumn) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');
    if (link) {
      if (link.getAttribute('target') === '_blank') {
        return;
      }
      if (onCellClick) {
        event.preventDefault();
        onCellClick({ row, column, field: column.field });
      }
    }
  }, [onCellClick]);

  const handleMobileTitleClick = useCallback((event: React.MouseEvent, row: any) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');
    if (link) {
      if (link.getAttribute('target') === '_blank') {
        return;
      }
      if (onCellClick) {
        event.preventDefault();
        const column = columns.find(c => c.field === mobiletitlefield);
        onCellClick({ row, column, field: mobiletitlefield || '' });
      }
    }
  }, [onCellClick, columns, mobiletitlefield]);

  const toggleCardExpand = useCallback((row: any, index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const key = getRowKey(row, index);
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  }, [getRowKey]);

  const focusPositionInput = useCallback((event: React.MouseEvent, row: any) => {
    const badge = event.currentTarget as HTMLElement;
    const input = badge.querySelector('.position-input') as HTMLInputElement;
    if (input) {
      badge.classList.add('editing');
      input.focus();
      input.select();
    }
  }, []);

  const onPositionChange = useCallback((event: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>, row: any) => {
    const input = event.target as HTMLInputElement;
    const badge = input.closest('.position-badge');
    if (badge) {
      badge.classList.remove('editing');
    }
    const newPosition = parseInt(input.value, 10);
    if (!isNaN(newPosition) && newPosition !== row.listIdx && newPosition >= 1) {
      row.listIdx = newPosition;
      onRowMoved?.(row);
    }
  }, [onRowMoved]);

  // --- Pagination handlers ---

  const goToFirstPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(1);
      onGoToPage?.(1);
    }
  }, [currentPage, onGoToPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      onGoToPage?.(newPage);
    }
  }, [currentPage, onGoToPage]);

  const goToNextPage = useCallback(() => {
    if (currentPage < (pager.numPages || 1)) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      onGoToPage?.(newPage);
    }
  }, [currentPage, pager.numPages, onGoToPage]);

  const goToLastPage = useCallback(() => {
    if (currentPage < (pager.numPages || 1)) {
      const lastPage = pager.numPages || 1;
      setCurrentPage(lastPage);
      onGoToPage?.(lastPage);
    }
  }, [currentPage, pager.numPages, onGoToPage]);

  const goToInputtedPage = useCallback((e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>) => {
    const page = Number((e.currentTarget as HTMLInputElement).value);
    if (Number.isInteger(page)) {
      if (page >= 1 && page <= (pager.numPages || 1) && page !== currentPage) {
        setCurrentPage(page);
        onGoToPage?.(page);
      }
    }
  }, [pager.numPages, currentPage, onGoToPage]);

  // --- Render ---

  return (
    <div
      ref={rootRef}
      className={
        'kst-data-grid' +
        (currentTheme ? ' ' + currentTheme : '') +
        (title ? ' has-title' : '')
      }
    >
      <div className={
        'grid-container' +
        (canScrollRight ? ' can-scroll-right' : '') +
        (canScrollLeft ? ' can-scroll-left' : '')
      }>
        {/* Header */}
        {showheader && (
          <div className={'grid-header' + (title ? ' has-title' : '')}>
            {title && <h1>{title}</h1>}
            <div className="header-actions">
              {headerbuttons.map(button => (
                <button
                  key={button.function}
                  className={'btn-action' + (button.function === 'delete' ? ' btn-danger' : '')}
                  onClick={() => buttonCommand(button.function)}
                >
                  {button.src && (
                    <img src={button.src} style={{ width: 14, height: 14, filter: 'brightness(0) invert(1)' }} />
                  )}
                  {button.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading State (initial load, no data yet) */}
        {(loading || rows.entries === null) && !hasRows && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && rows.entries !== null && !hasRows && (
          <div className="empty-state">
            No items found.
          </div>
        )}

        {/* Data Content (stays visible during refresh with overlay) */}
        {hasRows && (
          <div className="grid-content">
            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
              </div>
            )}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {showcheckbox && (
                      <th className="col-checkbox">
                        <input
                          type="checkbox"
                          className="grid-checkbox"
                          checked={allChecked}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    {showordercolumn && <th className="col-order">Order</th>}
                    {visibleColumns.map(column => (
                      <th
                        key={column.field}
                        className={
                          (column.sortable ? 'sortable' : '') +
                          (sortfield === column.field ? ' sorted' : '')
                        }
                        onClick={() => column.sortable ? onSort?.(column.field) : null}
                      >
                        {column.name}
                        {column.sortable && sortfield === column.field && (
                          <span className="sort-indicator">
                            {sortdirection === 'asc' ? '\u25B2' : '\u25BC'}
                          </span>
                        )}
                      </th>
                    ))}
                    {showstatustoggle && <th>Status</th>}
                    {desktopRowActions && !mobileactionsonly && <th className="col-actions">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rowEntries.map((row, rowIndex) => (
                    <tr key={row.id || row.code || rowIndex}>
                      {showcheckbox && (
                        <td className="col-checkbox">
                          <input
                            type="checkbox"
                            className="grid-checkbox"
                            checked={!!row.isChecked}
                            onChange={() => { row.isChecked = !row.isChecked; if (!row.isChecked) setAllChecked(false); else if (rowEntries.every((r: any) => r.isChecked)) setAllChecked(true); setCheckTick(t => t + 1); }}
                          />
                        </td>
                      )}
                      {showordercolumn && (
                        <td className="col-order">
                          <input
                            type="text"
                            className="order-input"
                            value={row.listIdx ?? ''}
                            disabled={!row.isListEnabled}
                            onChange={(e) => { row.listIdx = e.target.value; setCheckTick(t => t + 1); }}
                            onFocus={() => onOrderFocus(row)}
                            onBlur={() => rowMoved(row)}
                            onKeyUp={(e) => { if (e.key === 'Enter') rowMoved(row); }}
                          />
                        </td>
                      )}
                      {visibleColumns.map(column => (
                        <td
                          key={column.field}
                          title={getCellTitle(row, column)}
                          onClick={(e) => handleCellClick(e, row, column)}
                        >
                          <span dangerouslySetInnerHTML={{ __html: getCellValue(row, column) }} />
                        </td>
                      ))}
                      {showstatustoggle && (
                        <td>
                          <button
                            className={'btn-status-toggle ' + (row.isEnabled ? 'active' : 'inactive')}
                            onClick={() => toggleStatus(row)}
                          >
                            {row.isEnabled ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                      )}
                      {desktopRowActions && !mobileactionsonly && (
                        <td className="col-actions">
                          <div className="row-actions">
                            {desktopRowActions.map(action => (
                              <button
                                key={action.event}
                                className={'btn-row-action' + (action.class ? ' ' + action.class : '')}
                                onClick={(e) => { e.stopPropagation(); handleRowAction(action, row); }}
                                title={action.name}
                              >
                                {action.icon && <i className={'fa ' + action.icon}></i>}
                                {!action.iconOnly && <span>{action.name}</span>}
                              </button>
                            ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-cards">
              {rowEntries.map((row, rowIndex) => (
                <div
                  key={'card-' + (row.id || row.code || rowIndex)}
                  className={
                    'grid-card' +
                    (expandable && isRowExpanded(row, rowIndex) ? ' card-expanded' : '')
                  }
                >
                  <div className="card-header">
                    {showordercolumn && (
                      <span className="position-badge" onClick={(e) => focusPositionInput(e, row)}>
                        <span className="position-display">{row.listIdx}</span>
                        <input
                          type="text"
                          className="position-input"
                          defaultValue={row.listIdx}
                          onBlur={(e) => onPositionChange(e, row)}
                          onKeyUp={(e) => { if (e.key === 'Enter') onPositionChange(e as any, row); }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </span>
                    )}
                    <span
                      className="card-title"
                      onClick={(e) => handleMobileTitleClick(e, row)}
                      dangerouslySetInnerHTML={{ __html: getMobileTitleValue(row) }}
                    />
                  </div>
                  <div className="card-body">
                    {/* Key columns (always visible) or all mobile columns if not expandable */}
                    {(expandable ? mobileKeyDisplayColumns : mobileDisplayColumns)
                      .filter(column => hasCellValue(row, column))
                      .map(column => (
                        <div
                          key={column.field}
                          className={'card-row' + (column.fullWidth ? ' full-width' : '')}
                        >
                          <span className="card-label">{column.name}</span>
                          <span
                            className="card-value"
                            onClick={(e) => handleCellClick(e, row, column)}
                            dangerouslySetInnerHTML={{ __html: getCellValue(row, column) }}
                          />
                        </div>
                      ))}
                  </div>
                  {/* Expanded content */}
                  {expandable && isRowExpanded(row, rowIndex) && (
                    <div className="card-body card-body-expanded">
                      {mobileExpandedColumns
                        .filter(column => hasCellValue(row, column))
                        .map(column => (
                          <div
                            key={'exp-' + column.field}
                            className={'card-row' + (column.fullWidth ? ' full-width' : '')}
                          >
                            <span className="card-label">{column.name}</span>
                            <span
                              className="card-value"
                              onClick={(e) => handleCellClick(e, row, column)}
                              dangerouslySetInnerHTML={{ __html: getCellValue(row, column) }}
                            />
                          </div>
                        ))}
                    </div>
                  )}
                  {/* Expand/Collapse button */}
                  {expandable && mobileExpandedColumns.length > 0 && (
                    <button
                      className="btn-card-expand"
                      onClick={(e) => toggleCardExpand(row, rowIndex, e)}
                    >
                      {!isRowExpanded(row, rowIndex) ? (
                        <span><i className="fa fa-chevron-down"></i> Show More</span>
                      ) : (
                        <span><i className="fa fa-chevron-up"></i> Show Less</span>
                      )}
                    </button>
                  )}
                  {/* Card actions */}
                  {((rowactions && rowactions.length > 0) || showcheckbox || showstatustoggle) && (
                    <div className="card-actions">
                      {rowactions && rowactions.length > 0 && (
                        <div className="card-actions-row">
                          {rowactions.map(action => (
                            <button
                              key={action.event}
                              className={'btn-mobile-action' + (action.class ? ' ' + action.class : '')}
                              onClick={(e) => { e.stopPropagation(); handleRowAction(action, row); }}
                            >
                              {action.icon && <i className={'fa ' + action.icon}></i>}
                              {action.name}
                            </button>
                          ))}
                        </div>
                      )}
                      {(showcheckbox || showstatustoggle) && (
                        <div className="card-actions-row">
                          {showcheckbox && (
                            <label className="card-checkbox">
                              <input
                                type="checkbox"
                                className="grid-checkbox"
                                checked={!!row.isChecked}
                                onChange={() => { row.isChecked = !row.isChecked; if (!row.isChecked) setAllChecked(false); else if (rowEntries.every((r: any) => r.isChecked)) setAllChecked(true); setCheckTick(t => t + 1); }}
                              />
                              <span>Select for action</span>
                            </label>
                          )}
                          {showstatustoggle && (
                            <button
                              className={'btn-status-toggle ' + (row.isEnabled ? 'active' : 'inactive')}
                              onClick={() => toggleStatus(row)}
                            >
                              {row.isEnabled ? 'Active' : 'Inactive'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pagination */}
        {pager.isVisible && hasRows && (
          <div className="pagination-container">
            <div className="pagination-info">
              {paginationInfo}
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                disabled={currentPage <= 1}
                onClick={goToFirstPage}
              >
                <i className="fa fa-step-backward"></i>
              </button>
              <button
                className="pagination-btn"
                disabled={currentPage <= 1}
                onClick={goToPreviousPage}
              >
                <i className="fa fa-play fa-flip-horizontal"></i>
              </button>
              <span className="page-label">Page</span>
              <input
                type="text"
                className="page-input"
                defaultValue={currentPage}
                key={currentPage}
                onBlur={goToInputtedPage}
                onKeyUp={(e) => { if (e.key === 'Enter') goToInputtedPage(e as any); }}
              />
              <span className="page-label">of {pager.numPages || 1}</span>
              <button
                className="pagination-btn"
                disabled={currentPage >= (pager.numPages || 1)}
                onClick={goToNextPage}
              >
                <i className="fa fa-play"></i>
              </button>
              <button
                className="pagination-btn"
                disabled={currentPage >= (pager.numPages || 1)}
                onClick={goToLastPage}
              >
                <i className="fa fa-step-forward"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

(window as any).KstDataGridReact = KstDataGrid;
